import { useEffect, useState } from "react";
import { Button, Form, ListGroup, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  BinaryBlob,
  BundleManifest,
  BundleManifestComponent,
  BundleManifestRequirements,
  Release,
  ReleaseResponse,
} from "../../models";
import { HTTP_CLIENT } from "../../hooks";
import {
  COMPONENT_APPLICATION,
  COMPONENT_BOOTLOADER,
  COMPONENT_BUNDLE,
  COMPONENT_SE_FIDO,
  COMPONENT_SE_PIV,
  DATA_TYPE_BUNDLE_MANIFEST,
  SEMVER_PATTERN_FULL,
} from "../../utils";
import { ComponentEditorModal } from "./ComponentEditorModal";
import { ComponentListItem } from "./ComponentListItem";
import { RequirementsSection } from "./RequirementsSection";
import { VersionField } from "./VersionField";
import { RequiredMark } from "../FormFields";

interface Props {
  show: boolean;
  onHide: () => void;
}

const STANDARD_COMPONENT_TYPE_RANKS: Record<string, number> = {
  bootloader: 0,
  application: 1,
  se: 2,
};

export function GenerateManifestModal({ show, onHide: close }: Props) {
  const [bundleReleases, setBundleReleases] = useState<Release[]>([]);
  const [bootloaderReleases, setBootloaderReleases] = useState<Release[]>([]);
  const [applicationReleases, setApplicationReleases] = useState<Release[]>([]);
  const [fidoReleases, setFidoReleases] = useState<Release[]>([]);
  const [pivReleases, setPivReleases] = useState<Release[]>([]);

  const [templateReleaseId, setTemplateReleaseId] = useState<string | undefined>(undefined);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  const [bundleVersion, setBundleVersion] = useState<string | undefined>(undefined);
  const [requirementsEnabled, setRequirementsEnabled] = useState(false);
  const [requiredBundleVersion, setRequiredBundleVersion] = useState<string | undefined>(undefined);
  const [requiredTokenAppVersion, setRequiredTokenAppVersion] = useState<string | undefined>(undefined);
  const [requiredTmatAppVersion, setRequiredTmatAppVersion] = useState<string | undefined>(undefined);

  const [bundleComponents, setBundleComponents] = useState<BundleManifestComponent[]>([]);
  const [editorShown, setEditorShown] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | undefined>(undefined);

  const [isGenerating, setIsGenerating] = useState(false);

  // Clear all local state and invoke the parent's close callback. Required
  // because the modal does not unmount between opens — without this reset,
  // values from the previous session would leak into the next.
  const onHide = () => {
    setTemplateReleaseId(undefined);
    setIsLoadingTemplate(false);
    setBundleVersion(undefined);
    setRequirementsEnabled(false);
    setRequiredBundleVersion(undefined);
    setRequiredTokenAppVersion(undefined);
    setRequiredTmatAppVersion(undefined);
    setBundleComponents([]);
    setEditorShown(false);
    setEditingIndex(undefined);
    setIsGenerating(false);
    close();
  };

  const handleTemplateChange = async (releaseId: string) => {
    setTemplateReleaseId(releaseId);
    if (!releaseId) {
      setBundleVersion(undefined);
      setBundleComponents([]);
      setRequirementsEnabled(false);
      setRequiredBundleVersion(undefined);
      setRequiredTokenAppVersion(undefined);
      setRequiredTmatAppVersion(undefined);
      return;
    }
    setIsLoadingTemplate(true);
    try {
      const manifestBlob = await HTTP_CLIENT.get<BinaryBlob>({
        path: "/api/v2/binaryBlobs/search/getByReleaseAndDataType",
        query: { release: Number(releaseId), dataType: DATA_TYPE_BUNDLE_MANIFEST },
      });
      const manifest = JSON.parse(atob(manifestBlob.data)) as BundleManifest;
      setBundleVersion(manifest.version ? `${manifest.version}_copy` : undefined);
      setBundleComponents(manifest.components ?? []);
      setRequirementsEnabled(!!manifest.requirements);
      setRequiredBundleVersion(manifest.requirements?.bundle_version);
      setRequiredTokenAppVersion(manifest.requirements?.token_app_version);
      setRequiredTmatAppVersion(manifest.requirements?.tmat_app_version);
    } catch (e: any) {
      console.error(e.stack);
      toast.error(e.message);
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const componentsOutOfOrder = (() => {
    let maxRank = -1;
    for (const component of bundleComponents) {
      const rank = STANDARD_COMPONENT_TYPE_RANKS[component.type] ?? Number.MAX_SAFE_INTEGER;
      if (rank < maxRank) return true;
      if (rank > maxRank) maxRank = rank;
    }
    return false;
  })();

  const moveComponent = (index: number, moveUp: boolean) => {
    const target = index + (moveUp ? -1 : 1);
    if (target < 0 || target >= bundleComponents.length) return;
    const next = [...bundleComponents];
    [next[index], next[target]] = [next[target], next[index]];
    setBundleComponents(next);
  };

  const removeComponent = (index: number) => {
    setBundleComponents(bundleComponents.filter((_, i) => i !== index));
  };

  const handleEditorSave = (component: BundleManifestComponent) => {
    if (editingIndex === undefined) {
      setBundleComponents([...bundleComponents, component]);
    } else {
      const existingComponents = [...bundleComponents];
      existingComponents[editingIndex] = component;
      setBundleComponents(existingComponents);
    }
    setEditorShown(false);
    setEditingIndex(undefined);
  };

  const generateManifest = async () => {
    setIsGenerating(true);
    try {
      const bundleManifest: BundleManifest = {
        version: bundleVersion?.trim() ?? "",
        components: bundleComponents,
      };
      const requirements: BundleManifestRequirements = {};
      if (requirementsEnabled) {
        if (requiredBundleVersion) requirements.bundle_version = requiredBundleVersion;
        if (requiredTokenAppVersion) requirements.token_app_version = requiredTokenAppVersion;
        if (requiredTmatAppVersion) requirements.tmat_app_version = requiredTmatAppVersion;
      }

      if (Object.keys(requirements).length !== 0) {
        bundleManifest.requirements = requirements;
      } else if (requirementsEnabled) {
        setRequirementsEnabled(false);
      }

      const blob = new Blob([JSON.stringify(bundleManifest, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `generated_bundle-${bundleVersion ?? "unknown"}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error(e.stack);
      toast.error(e.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const validManifestInputs = () => {
    if (!bundleVersion || !bundleVersion.trim() || bundleComponents.length === 0) return false;
    if (
      requirementsEnabled &&
      ((requiredTokenAppVersion && !SEMVER_PATTERN_FULL.test(requiredTokenAppVersion)) ||
        (requiredTmatAppVersion && !SEMVER_PATTERN_FULL.test(requiredTmatAppVersion)))
    ) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!show) return;
    const fetchReleases = async (model: number) => {
      const response = await HTTP_CLIENT.get<ReleaseResponse>({
        path: "/api/v2/releases/search/findByModel",
        query: { model },
      });
      return response._embedded.releases;
    };
    void (async () => {
      try {
        const [bundle, bootloader, application, fido, piv] = await Promise.all([
          fetchReleases(COMPONENT_BUNDLE),
          fetchReleases(COMPONENT_BOOTLOADER),
          fetchReleases(COMPONENT_APPLICATION),
          fetchReleases(COMPONENT_SE_FIDO),
          fetchReleases(COMPONENT_SE_PIV),
        ]);
        setBundleReleases(bundle);
        setBootloaderReleases(bootloader);
        setApplicationReleases(application);
        setFidoReleases(fido);
        setPivReleases(piv);
      } catch (e: any) {
        console.error(e.stack);
        toast.error(e.message);
      }
    })();
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Generate Manifest</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group controlId="templateRelease" className="mb-3">
            <Form.Label>Template</Form.Label>
            <Form.Select
              size="sm"
              value={templateReleaseId ?? ""}
              disabled={isLoadingTemplate}
              onChange={(e) => void handleTemplateChange(e.target.value)}
            >
              <option value="">-- select bundle release --</option>
              {bundleReleases.map((release) => (
                <option key={`template-release-${release.id}`} value={release.id}>
                  {release.version}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Selecting a bundle release populates the fields below with its manifest data.
            </Form.Text>
          </Form.Group>

          <VersionField
            controlId="bundleVersion"
            label="Version"
            placeholder="Enter version in YYYY.MM.XX format  (e.g. 2026.3.1)"
            value={bundleVersion}
            onChange={setBundleVersion}
            className="mb-3"
          />

          <RequirementsSection
            enabled={requirementsEnabled}
            onEnabledChange={setRequirementsEnabled}
            bundleReleases={bundleReleases}
            requiredBundleVersion={requiredBundleVersion}
            onRequiredBundleVersionChange={setRequiredBundleVersion}
            requiredTokenAppVersion={requiredTokenAppVersion}
            onRequiredTokenAppVersionChange={setRequiredTokenAppVersion}
            requiredTmatAppVersion={requiredTmatAppVersion}
            onRequiredTmatAppVersionChange={setRequiredTmatAppVersion}
          />

          <Form.Group className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <Form.Label className="mb-0 me-2">Components</Form.Label>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  setEditingIndex(undefined);
                  setEditorShown(true);
                }}
              >
                Add Component
              </Button>
            </div>
            {bundleComponents.length === 0 ? (
              <div className="text-muted">
                <RequiredMark /> No components added.
              </div>
            ) : (
              <ListGroup>
                {bundleComponents.map((component, index) => (
                  <ComponentListItem
                    key={`component-${index}`}
                    component={component}
                    isFirst={index === 0}
                    isLast={index === bundleComponents.length - 1}
                    onMoveUp={() => moveComponent(index, true)}
                    onMoveDown={() => moveComponent(index, false)}
                    onEdit={() => {
                      setEditingIndex(index);
                      setEditorShown(true);
                    }}
                    onRemove={() => removeComponent(index)}
                  />
                ))}
              </ListGroup>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        {componentsOutOfOrder && (
          <div className="text-warning me-auto small">
            ⚠ Components are not in expected order. Recommended order is bootloader, application, then se packages.
          </div>
        )}
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={() => void generateManifest()}
          disabled={isGenerating || !validManifestInputs()}
        >
          {isGenerating ? "Generating..." : "Generate"}
        </Button>
      </Modal.Footer>

      <ComponentEditorModal
        show={editorShown}
        close={() => {
          setEditorShown(false);
          setEditingIndex(undefined);
        }}
        onSave={handleEditorSave}
        existingComponent={
          editingIndex !== undefined ? bundleComponents[editingIndex] : undefined
        }
        bootloaderReleases={bootloaderReleases}
        applicationReleases={applicationReleases}
        fidoReleases={fidoReleases}
        pivReleases={pivReleases}
      />
    </Modal>
  );
}
